import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Table, ButtonGroup, Button, Dropdown } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import ExportButton from "./ExportButton";
import { exportToExcel } from "./exportExcel";

const percent = (num, den) => {
    const n = Number(num || 0);
    const d = Number(den || 0);
    if (d <= 0) return 0;
    return (n / d) * 100;
};

const GeneralSummary = props => {
    const { className, innerRef, generalSummary, ...attributes } = props;

    const [info, setInfo] = useState({});
    const [viewMode, setViewMode] = useState("table");
    const [chartSlice, setChartSlice] = useState("operator");

    useEffect(() => {
        setInfo({ ...(generalSummary || {}) });
    }, [generalSummary]);

    const totals = useMemo(() => {
        const total = Number(info?.total || 0);
        const unimedTotal = Number(info?.unimed?.total || 0);
        const sus = Number(info?.sus || 0);
        const particular = Number(info?.particular || 0);

        const otherComplex = Number(info?.other?.complex || 0);
        const unimedComplex = Number(info?.unimed?.complex || 0);
        const otherNormal = Number(info?.other?.normal || 0);
        const unimedNormal = Number(info?.unimed?.normal || 0);
        const otherEnferm = Number(info?.other?.enferm || 0);
        const unimedEnferm = Number(info?.unimed?.enferm || 0);
        const otherApto = Number(info?.other?.apto || 0);
        const unimedApto = Number(info?.unimed?.apto || 0);

        const outros = Math.max(total - unimedTotal - sus - particular, 0);

        return {
            total,
            operator: {
                labels: ["Unimed", "SUS", "Particular", "Outros"],
                data: [unimedTotal, sus, particular, outros]
            },
            complexity: {
                labels: ["Complexa", "Convencional"],
                data: [otherComplex + unimedComplex, otherNormal + unimedNormal]
            },
            accommodation: {
                labels: ["Enfermaria", "Apartamento"],
                data: [otherEnferm + unimedEnferm, otherApto + unimedApto]
            }
        };
    }, [info]);

    const chartBundle = useMemo(() => {
        const source = totals[chartSlice] || { labels: [], data: [] };

        const options = {
            chart: { type: "donut", toolbar: { show: false } },
            labels: source.labels,
            legend: { position: "bottom" },
            dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(1)}%` },
            tooltip: {
                y: {
                    formatter: (val) => `${Number(val).toLocaleString("pt-BR")}`
                }
            },
            title: {
                text:
                    chartSlice === "operator"
                        ? "Distribuição por Operadora"
                        : chartSlice === "complexity"
                        ? "Distribuição por Complexidade"
                        : "Distribuição por Acomodação",
                style: { fontFamily: "poppins", fontWeight: 600 }
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: "65%",
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label: "Total",
                                formatter: () => `${totals.total.toLocaleString("pt-BR")}`
                            }
                        }
                    }
                }
            }
        };

        return { options, series: source.data };
    }, [totals, chartSlice]);

    function handleExport() {
        const columns = [
            ["Convênio Complexa", info.other?.complex],
            ["Convênio Convencional", info.other?.normal],
            ["Convênio Enferm", info.other?.enferm],
            ["Convênio Apto", info.other?.apto],
            ["Unimed Total", info.unimed?.total],
            ["Unimed Enferm", info.unimed?.enferm],
            ["Unimed Apto", info.unimed?.apto],
            ["Unimed Convencional", info.unimed?.normal],
            ["Unimed Complexa", info.unimed?.complex],
            ["SUS", info.sus],
            ["Particular", info.particular]
        ];

        const row = { "Geral Convênios": Number(info.total || 0) };
        columns.forEach(([label, value]) => {
            row[label] = Number(value || 0);
            row[`% ${label}`] = Number(percent(value, info.total).toFixed(2));
        });

        exportToExcel([row], "Comparacoes Gerais", "comparacoes_gerais");
    }

    return (
        <>
            <div className="row">
                <div className="col-xl-8">
                    <div className="card-header border-0 flex-wrap pb-2">
                        <div className="chart-title mb-2 ">
                            <h2 className="heading">Comparações Gerais</h2>
                        </div>
                    </div>
                </div>
                <div className="col-xl-4">
                    <div className="d-flex justify-content-end gap-2 align-items-center flex-wrap">
                        <ButtonGroup className="me-2" aria-label="Dimensão do gráfico" hidden={viewMode !== "chart"}>
                            <Button variant={chartSlice === "operator" ? "primary" : "light"} onClick={() => setChartSlice("operator")}>
                                Operadora
                            </Button>
                            <Button variant={chartSlice === "complexity" ? "primary" : "light"} onClick={() => setChartSlice("complexity")}>
                                Complexidade
                            </Button>
                            <Button variant={chartSlice === "accommodation" ? "primary" : "light"} onClick={() => setChartSlice("accommodation")}>
                                Acomodação
                            </Button>
                        </ButtonGroup>
                        <ButtonGroup aria-label="View Mode">
                            <Button variant={viewMode === "table" ? "primary" : "light"} onClick={() => setViewMode("table")}>
                                Tabela
                            </Button>
                            <Button variant={viewMode === "chart" ? "primary" : "light"} onClick={() => setViewMode("chart")}>
                                Indicador
                            </Button>
                        </ButtonGroup>
                        <ExportButton onClick={handleExport} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    <div className="card-body pt-0 custome-tooltip">
                        {viewMode === "table" ? (
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th className="text-center"><strong>Geral Convênios</strong></th>
                                        <th className="text-center"><strong>Convênio Complexa</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Convênio Convencional</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Convênio Enferm</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Convênio Apto</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Unimed Total</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Unimed Enferm</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Unimed Apto</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Unimed Convencional</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Unimed Complexa</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>SUS</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                        <th className="text-center"><strong>Particular</strong></th>
                                        <th className="text-center"><strong>%</strong></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr key="main_tr">
                                        <td><div className="text-center">{Number(info.total || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{Number(info.other?.complex || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.other?.complex, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.other?.normal || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.other?.normal, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.other?.enferm || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.other?.enferm, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.other?.apto || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.other?.apto, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.unimed?.total || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.unimed?.total, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.unimed?.enferm || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.unimed?.enferm, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.unimed?.apto || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.unimed?.apto, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.unimed?.normal || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.unimed?.normal, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.unimed?.complex || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.unimed?.complex, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.sus || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.sus, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                        <td><div className="text-center">{Number(info.particular || 0).toLocaleString("pt-BR")}</div></td>
                                        <td><div className="text-center">{percent(info.particular, info.total).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %</div></td>
                                    </tr>
                                </tbody>
                            </Table>
                        ) : (
                            <ReactApexChart options={chartBundle.options} series={chartBundle.series} type="donut" height={380} />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

GeneralSummary.propTypes = {
    children: PropTypes.node,
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
    innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    generalSummary: PropTypes.object
};

GeneralSummary.defaultProps = { generalSummary: {} };

export default GeneralSummary;
